import { ElementRef } from '@angular/core';
import { BehaviorSubject, fromEvent, Observable, Subject } from 'rxjs';
import { Subscription } from 'rxjs';
import { FileType } from '@fi-shared/types';
import { getAcceptByFileType } from '@fi-shared/utils/get-accept-by-file-type';
import { FiFileUpload2Service } from '@fi-shared/fi-insert-media/fi-file-upload2.service';

export enum FiDndEventType {
    DragEnter = 0,
    DragOver = 1,
    DragLeave = 2,
    Drop = 3,
}

export interface FiDndDetails {
    dndEventType: FiDndEventType;
    isDndAllowed?: boolean;
}

export class FiDndHelper {
    private elementRef: ElementRef<HTMLElement>;
    private fileUpload2Service: FiFileUpload2Service;
    private allowedMimeTypes: string;
    private subscriptions: Subscription[] = [];

    private _dragging$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    get dragging$(): Observable<boolean> {
        return this._dragging$.asObservable();
    }

    private dropping$: Subject<DragEvent> = new Subject<DragEvent>();
    get onDropping$(): Observable<any> {
        return this.dropping$.asObservable();
    }

    private _dndDetails$: Subject<FiDndDetails> = new Subject<FiDndDetails>();
    get onDndDetails$() {
        return this._dndDetails$.asObservable();
    }

    public initDndEventSubscriptions(
        elementRef: ElementRef<HTMLElement>,
        fileUpload2Service: FiFileUpload2Service,
        args?: { allowedFileTypes?: FileType[] },
    ): void {
        this.elementRef = elementRef;
        this.fileUpload2Service = fileUpload2Service;

        if (args?.allowedFileTypes) {
            this.allowedMimeTypes = getAcceptByFileType(args.allowedFileTypes, undefined);
        }

        this.dragEnterStreamListening();
        this.dragLeaveStreamListening();
        this.dragOverStreamListening();
        this.dropStreamListening();
    }

    public unsubscribeFromDndEvents(): void {
        this.subscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
        this.subscriptions.length = 0;
    }

    private isDndAllowed(dataTransfer: DataTransfer) {
        if (!dataTransfer.items.length || dataTransfer.items[0].kind !== 'file') {
            return false;
        }
        const result = this.fileUpload2Service.isUploadAllowed(dataTransfer.items[0].type, this.allowedMimeTypes);
        return result;
    }

    private checkAllowDnd(event: DragEvent): boolean {
        event.preventDefault();
        if (this.isDndAllowed(event.dataTransfer)) {
            event.dataTransfer.dropEffect = 'copy';
            return true;
        } else {
            event.dataTransfer.dropEffect = 'none';
            return false;
        }
    }

    private dragEnterStreamListening(): void {
        this.subscriptions.push(
            fromEvent(this.elementRef.nativeElement, 'dragenter').subscribe((event: DragEvent) => {
                const isDndAllowed = this.checkAllowDnd(event);
                if (isDndAllowed) {
                    this._dragging$.next(true);
                }
                this._dndDetails$.next({
                    dndEventType: FiDndEventType.DragEnter,
                    isDndAllowed: isDndAllowed,
                });
            }),
        );
    }

    private dragOverStreamListening(): void {
        this.subscriptions.push(
            fromEvent(this.elementRef.nativeElement, 'dragover').subscribe((event: DragEvent) => {
                const isDndAllowed = this.checkAllowDnd(event);
                if (isDndAllowed) {
                    this._dragging$.next(true);
                }
                this._dndDetails$.next({
                    dndEventType: FiDndEventType.DragOver,
                    isDndAllowed: isDndAllowed,
                });
            }),
        );
    }

    private dragLeaveStreamListening(): void {
        this.subscriptions.push(
            fromEvent(this.elementRef.nativeElement, 'dragleave').subscribe((event: DragEvent) => {
                event.preventDefault();
                this._dragging$.next(false);

                this._dndDetails$.next({
                    dndEventType: FiDndEventType.DragLeave,
                });
            }),
        );
    }

    private dropStreamListening(): void {
        this.subscriptions.push(
            fromEvent(this.elementRef.nativeElement, 'drop').subscribe((event: DragEvent) => {
                event.preventDefault();
                this.dropping$.next(event);
                this._dragging$.next(false);

                this._dndDetails$.next({
                    dndEventType: FiDndEventType.Drop,
                });
            }),
        );
    }
}
